"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useMemo } from "react";
import type { CompanyFiltersParams } from "@artha/utils";
import { queryKeys } from "@/lib/queryKeys";
import type { JobCountParams, JobCountApiResponse, JobFeedParams, FiltersParams } from "./types";
import { fetchCompanyFilters, fetchJobCount, fetchJobs, fetchPublicJobs, fetchFilters } from "./fetchers";

// ============================================
// Standard React Query Hooks
// ============================================

/**
 * React Query hook for fetching jobs (authenticated).
 * If no params provided, backend uses creator's niche/preferences.
 */
export const useJobsQuery = (params: JobFeedParams = {}) =>
  useQuery({
    queryKey: queryKeys.jobs(params),
    queryFn: () => fetchJobs(params),
  });

/**
 * React Query hook for fetching jobs on public profile.
 */
export const usePublicJobsQuery = (username: string, params: JobFeedParams = {}) =>
  useQuery({
    queryKey: queryKeys.publicJobs(username, params),
    queryFn: () => fetchPublicJobs(username, params),
    enabled: !!username,
  });

/**
 * React Query hook for fetching filter options.
 */
export const useFiltersQuery = (params: FiltersParams = {}) =>
  useQuery({
    queryKey: queryKeys.jobFilters(params),
    queryFn: () => fetchFilters(params),
  });

export const useCompanyFiltersQuery = (
  params: CompanyFiltersParams = {},
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: queryKeys.companyFilters(params),
    queryFn: () => fetchCompanyFilters(params),
    enabled: options.enabled ?? true,
  });

// ============================================
// Optimized Job Count Hook with Debouncing
// ============================================

interface UseOptimizedJobCountOptions {
  enabled?: boolean;
  debounceMs?: number;
}

/**
 * Optimized job count hook with debouncing
 * Prevents excessive API calls during filter changes
 */
export const useOptimizedJobCount = (
  params: JobCountParams,
  options: UseOptimizedJobCountOptions = {}
) => {
  const {
    enabled = false,
    debounceMs = 500,
  } = options;

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  // Create a stable query key for predictable refetches
  const queryKey = useMemo(() => queryKeys.jobCount(params), [params]);

  // Debounced refetch function
  const debouncedRefetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey });
    }, debounceMs);
  }, [queryClient, queryKey, debounceMs]);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchJobCount(params),
    enabled,
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
  });

  return {
    ...query,
    debouncedRefetch,
  };
};

// ============================================
// Batch Job Count Hook
// ============================================

interface BatchJobCountParams {
  requests: JobCountParams[];
  enabled?: boolean;
}

/**
 * Hook for batching multiple job count requests
 * Useful when you need counts for multiple filter combinations
 */
export const useBatchJobCount = ({ requests, enabled = false }: BatchJobCountParams) => {
  const queries = requests.map((params) => ({
    queryKey: queryKeys.jobCount(params),
    queryFn: () => fetchJobCount(params),
    enabled,
  }));

  // Use React Query's parallel queries feature
  const results = useQuery({
    queryKey: ["batchJobCount", requests],
    queryFn: async () => {
      const promises = requests.map((params) => fetchJobCount(params));
      return Promise.all(promises);
    },
    enabled: enabled && requests.length > 0,
  });

  return {
    data: results.data,
    isLoading: results.isLoading,
    error: results.error,
    refetch: results.refetch,
  };
};

// ============================================
// Predictive Job Count Hook
// ============================================

interface UsePredictiveJobCountOptions {
  baseParams: JobCountParams;
  enabled?: boolean;
}

/**
 * Hook that prefetches likely filter variations
 * Improves perceived performance by preloading common combinations
 */
export const usePredictiveJobCount = ({ baseParams, enabled = false }: UsePredictiveJobCountOptions) => {
  const queryClient = useQueryClient();

  // Generate likely variations based on base params
  const variations = useMemo(() => {
    const variants: JobCountParams[] = [];

    // Add variations with different work modes
    const workModes = ["remote", "hybrid", "onsite"];
    workModes.forEach(mode => {
      variants.push({ ...baseParams, work_mode: mode });
    });

    // Add variations with different job types
    const jobTypes = ["full-time", "part-time", "contract"];
    jobTypes.forEach(type => {
      variants.push({ ...baseParams, job_type: type });
    });

    // Add variations with different salary ranges
    const salaryRanges = [50000, 75000, 100000, 150000];
    salaryRanges.forEach(salary => {
      variants.push({ ...baseParams, salary_min: salary });
    });

    return variants.slice(0, 6); // Limit to 6 variations to avoid too many requests
  }, [baseParams]);

  // Prefetch variations when enabled
  const prefetchVariations = useCallback(() => {
    if (!enabled) return;

    variations.forEach((params) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.jobCount(params),
        queryFn: () => fetchJobCount(params),
      });
    });
  }, [queryClient, variations, enabled]);

  return {
    prefetchVariations,
    variations,
  };
};

// ============================================
// Smart Job Count Hook (Combines all optimizations)
// ============================================

interface UseSmartJobCountOptions extends UseOptimizedJobCountOptions {
  enablePredictive?: boolean;
  enableBatching?: boolean;
}

/**
 * Smart job count hook that combines all optimizations:
 * - Debouncing
 * - Predictive prefetching
 * - Request deduplication (built into React Query)
 */
export const useSmartJobCount = (
  params: JobCountParams,
  options: UseSmartJobCountOptions = {}
) => {
  const {
    enablePredictive = false,
    enableBatching = false,
    ...optimizedOptions
  } = options;

  // Main optimized query
  const optimizedQuery = useOptimizedJobCount(params, optimizedOptions);

  // Predictive prefetching
  const { prefetchVariations } = usePredictiveJobCount({
    baseParams: params,
    enabled: enablePredictive,
  });

  // Auto-prefetch when query succeeds
  const enhancedQuery = {
    ...optimizedQuery,
    data: optimizedQuery.data,
    // Trigger prefetching when main query succeeds
    onSuccess: useCallback((data: JobCountApiResponse) => {
      if (enablePredictive && data) {
        prefetchVariations();
      }
    }, [enablePredictive, prefetchVariations]),
  };

  return enhancedQuery;
};
